import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import LegalLinks from '../LegalLinks';
import { useLanguage } from '../../i18n';

// ─────────────────────────────────────────────────────────────────────────────
// PAI Data & AI Addendum — hosted on the PAI domain, linked from the iubenda
// Terms and Conditions via "Advanced incorporation of other documents".
//
// The URL path carries both "legal" and "pai" by requirement:
//     /legal/pai-data-ai-addendum
//
// 🛑 THE BODY TEXT IS A LEGAL INSTRUMENT that the Terms incorporate by
// reference, so "improving" a sentence here silently changes what users
// agreed to — wording changes only through an explicit, approved version
// bump, never a drive-by edit. v1.0 (19 September 2026) was verbatim from
// the signed PDF. **v1.0 → v1.1 (30 September 2026)** is the FIRST such
// bump, approved via Trello card #418 ("Privacy paperwork") plus the
// shared-content decision on #415; every wording change below traces to
// one of those two cards, and nothing else was touched. The five changes
// (two were refined once more inside the same review, before merge — see
// items 4 and 5):
//   1. §4.1/§8 — disclose that AI requests are processed by Vertex AI in
//      Google Cloud's *global* region (not only us-central1).
//   2. §8 — add iubenda as a sub-processor (it stores the Consent Database
//      record of what a user agreed to — a GDPR Art. 28/13 disclosure,
//      distinct from the "generated by iubenda" credit on the documents it
//      hosts, which only names who authored the template).
//   3. §5.1(d) — drop the exact day-count for backup retention on deletion
//      (PO decision: naming "7 days" creates a promise we cannot honestly
//      keep if an incident ever postpones a rotation) while keeping the
//      substance: backups purge on routine rotation and are never used to
//      restore an individual account.
//   4. §5.2 — add a bullet naming the retained SIGNUP CONSENT RECORD as
//      evidence under GDPR Art. 7(1)/17(3)(e) (BE/ACCOUNT_DELETION_AUDIT.md
//      §7.2 already proposed this; the aggregate-usage-statistics bullet it
//      also proposed already existed, so it was left alone rather than
//      duplicated) and a SECOND new bullet, also drawn from the same PO
//      "no exact day-counts" decision, for operational/security logs that
//      merely reference the account (retained briefly, incl. a record the
//      account was deleted — no day-count named). §5.1(c) was reworded to
//      be consistent with this: it used to claim ALL operational logs are
//      purged "regardless of the retention periods that would otherwise
//      apply", which both overclaimed (Cloud Logging cannot delete entries
//      per user — BE/ACCOUNT_DELETION_AUDIT.md §6.6) and now contradicted
//      the new §5.2 bullet. §5.1(c) now says only the CONTENT-BEARING logs
//      (Section 6's AI-processing/security log text) are purged immediately;
//      logs that merely reference the account, without content, are the
//      ones §5.2's new bullet covers.
//   5. §5 — add "Your subscription" (cancelled + payment method removed on
//      deletion) and rewrite "Content you shared with others" to Option B
//      from card #415: a TICKET you created inside ANOTHER user's shared
//      workspace stays there, shown as authored by a deleted user — but a
//      COMMENT you wrote there does NOT survive (`board_ticket_comments.
//      user_id` is `ON DELETE CASCADE`, unchanged by #415, so it is deleted
//      with the rest of your content; a ticket is a container the team
//      keeps working in, a comment is a leaf you authored). Any board,
//      financial book, note, team or shared conversation the user CREATED
//      is erased for everyone; a record another user made about them (e.g.
//      a contact card) stays theirs with the link removed.
//   ⚠️ Item 5 SUPERSEDES the opposite sentence BE/ACCOUNT_DELETION_AUDIT.md
//   §7.2 originally proposed ("Content you placed in another user's shared
//   workspace is deleted with your account") — that draft predates #415's
//   decision, and §6.3 of that same audit has since been updated to match
//   (Option B, implemented, no DDL): the page's wording and the code agree.
//
// **v1.1 → v1.2 (approved by Chris 2026-10-23, Trello #460, Option C)** — ONE
// sentence, §6 "AI processing logs", wording supplied verbatim by the PO (16
// Oct): usage figures now stay linked to the account while it is active (the
// Energy history) and are anonymised only on account deletion (§5.2). v1.1
// promised anonymisation at 90 days for everyone, which would have meant
// detaching ~9,000 usage rows from ACTIVE accounts; the PO chose to keep the
// history instead and change the promise. The text of the logs is still
// deleted after 90 days (that purge already runs). Nothing else changed.
//   🛑 EFFECTIVE_DATE below is a PLACEHOLDER until the production promotion:
//   v1.2 takes effect the day it is published in production, so whoever runs
//   promote_prod.sh sets it to that date (e.g. '30 October 2026') first.
//
// Two older transcription decisions, both faithfulness-over-tidiness and both
// still true of the v1.0 text this document builds on:
//   • Section 7's items are lettered (e) (f) (g) in the source — the PDF's list
//     counter continued from 5.1's (a)–(d) rather than restarting. Reproduced
//     as-is; renumbering them would be an edit to a legal document. Flagged to
//     the PO as an upstream typo to fix in the next version. New v1.1 content
//     was deliberately written as unlettered paragraphs rather than extending
//     this list, so the (a)–(d)/(e)–(g) scheme is undisturbed.
//   • The tabular sections (6 and 8) are rendered as <dl> pairs rather than a
//     <table>. The PAIRS and their wording are identical to the PDF (plus the
//     new iubenda row); a two-column table cannot shrink below its widest
//     cell, and this page has to stay readable on a phone (#243's containment
//     rule).
//
// The page is PUBLIC: it is routed outside the auth gate, because the document
// it carries is incorporated into the Terms that a signed-OUT visitor is being
// asked to accept, and because the iubenda T&C links to it for anyone at all.
// ─────────────────────────────────────────────────────────────────────────────

const PAGE_TITLE = 'PAI Data and AI Addendum';

// v1.2 effective date = the day it goes live in PRODUCTION (#460). Placeholder
// until the promotion — see the v1.2 note above.
const EFFECTIVE_DATE = '25 October 2026';

// Shared text rhythm. Legal prose is read in long stretches, so the measure is
// narrow and the leading generous — the opposite of the app's dense chrome.
const P = 'mt-4 text-[15px] leading-[1.75] text-pai-body';
const H2 = 'mt-12 text-[20px] font-extrabold tracking-[-0.02em] text-white';
const H3 = 'mt-8 text-[16px] font-bold text-white';
const UL = 'mt-4 space-y-2.5 text-[15px] leading-[1.75] text-pai-body';
const LI = 'relative pl-5 before:absolute before:left-0 before:top-[0.68em] before:h-[5px] before:w-[5px] before:rounded-full before:bg-white/35';

// A lettered clause, e.g. "(a) All your content …".
const Clause = ({ mark, children }) => (
    <li className="relative pl-9 text-[15px] leading-[1.75] text-pai-body">
        <span className="absolute left-0 top-0 font-mono text-[13px] text-pai-meta">({mark})</span>
        {children}
    </li>
);

// A term/definition pair — the faithful rendering of the PDF's two-column rows.
const Pair = ({ term, children }) => (
    <div className="border-t border-pai-hairline py-4 first:border-t-0 first:pt-0 sm:flex sm:gap-6">
        <dt className="text-[14px] font-bold text-white sm:w-[34%] sm:flex-none">{term}</dt>
        <dd className="mt-1.5 min-w-0 text-[15px] leading-[1.7] text-pai-body sm:mt-0">{children}</dd>
    </div>
);

const DataAiAddendumPage = () => {
    const { t } = useLanguage();

    // The document's own name in the tab — it is reached directly from the
    // iubenda Terms, so it is frequently the first PAI page a reader opens.
    useEffect(() => {
        const previous = document.title;
        document.title = PAGE_TITLE;
        return () => { document.title = previous; };
    }, []);

    return (
        <div
            data-testid="legal-addendum-page"
            className="relative min-h-dvh bg-pai-void text-white"
        >
            <div
                aria-hidden="true"
                className="fixed inset-0 bg-cover bg-center"
                style={{ backgroundImage: "url('/wallpaper.jpg')" }}
            />
            <div aria-hidden="true" className="fixed inset-0 bg-[rgba(5,7,10,0.78)]" />

            <div className="relative mx-auto w-full max-w-[860px] px-6 py-10 sm:py-14">
                {/* Masthead — the PAI mark, the section it belongs to, and the
                    way back. A reader who arrived from the iubenda Terms has no
                    app chrome around them, so the page carries its own. */}
                <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                        <span
                            aria-hidden="true"
                            className="flex h-10 w-10 flex-none select-none items-center justify-center rounded-full bg-white text-[13px] font-extrabold tracking-[-0.02em] text-pai-void"
                        >
                            PAI
                        </span>
                        <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-pai-meta">
                            {t('legalEyebrow', 'Legal')}
                        </span>
                    </div>
                    <Link
                        to="/"
                        data-testid="legal-addendum-back"
                        className="rounded-ctl border border-pai-subtle bg-white/[0.04] px-4 py-2 text-[13px] font-semibold text-pai-secondary transition-colors hover:border-pai-strong hover:text-white"
                    >
                        {t('legalBackToPai', 'Back to PAI')}
                    </Link>
                </div>

                <article className="mt-8 rounded-modal border border-white/[0.14] bg-white/[0.03] px-6 py-9 backdrop-blur-tile sm:px-11 sm:py-12">
                    <h1
                        data-testid="legal-addendum-title"
                        className="text-[32px] font-extrabold leading-[1.15] tracking-[-0.03em] text-white sm:text-[38px]"
                    >
                        Data &amp; AI Addendum
                    </h1>
                    <p className="mt-3 font-mono text-[12.5px] text-pai-meta">
                        Effective date: {EFFECTIVE_DATE} · Version 1.2 · supersedes v1.1 of 30 September 2026
                    </p>

                    <p className={P}>
                        This Data &amp; AI Addendum (“Addendum”) supplements the Terms and Conditions of
                        PAI (workbypai.com) and forms part of the agreement between The Title AI LLC
                        (“we”, “us”, “the Owner”) and the User. Where this Addendum conflicts with the
                        general Terms and Conditions, this Addendum prevails. Capitalised terms not
                        defined here have the meaning given in the Terms and Conditions.
                    </p>
                    <p className={P}>
                        PAI is built on a simple principle: your data belongs to you. This Addendum
                        explains how we handle it, what role artificial intelligence plays, and what
                        commitments we make to protect your information.
                    </p>

                    {/* 1 */}
                    <h2 className={H2}>1. Use of Artificial Intelligence</h2>
                    <p className={P}>
                        PAI uses artificial intelligence to provide its core functionality. When you
                        interact with the Service — by sending messages, uploading documents, recording
                        voice notes, or using any feature that analyses your content — that content is
                        processed by AI systems. This includes:
                    </p>
                    <ul className={UL}>
                        <li className={LI}>Reading, analysing and responding to your messages and queries</li>
                        <li className={LI}>Analysing, summarising and extracting information from documents, receipts and images you upload</li>
                        <li className={LI}>Transcribing and processing your voice recordings</li>
                        <li className={LI}>Generating search results, calendar entries, contact records, financial summaries and other outputs based on your content</li>
                        <li className={LI}>Creating text embeddings of your content to enable search within your account</li>
                    </ul>
                    <p className={P}>
                        <strong className="font-bold text-white">
                            You are always interacting with an AI system.
                        </strong>{' '}
                        PAI is an AI-driven assistant. All automated analysis, responses and suggestions
                        are generated by artificial intelligence, not by a human.
                    </p>

                    {/* 2 */}
                    <h2 className={H2}>2. Your Content Is Never Used to Train AI Models</h2>
                    <p className={P}>
                        We do not use your content to train, fine-tune, improve or develop any
                        machine-learning or artificial intelligence model — ours or anyone else’s. This
                        commitment applies without exception to all User content: messages, documents,
                        images, voice recordings, notes, contacts, calendar data and financial records.
                    </p>
                    <p className={P}>
                        We contractually require the same commitment from every sub-processor that
                        handles your content. Specifically:
                    </p>
                    <ul className={UL}>
                        <li className={LI}>Our AI provider processes your content solely to generate a response to your request and does not retain it for model training.</li>
                        <li className={LI}>Our cloud infrastructure provider stores and transmits your content but does not use it for any purpose beyond providing the infrastructure service.</li>
                        <li className={LI}>No sub-processor is permitted to use your content to improve, train or benchmark its own or any third party’s models or products.</li>
                    </ul>
                    <p className={P}>
                        If we ever change AI provider, the replacement will be held to the same
                        no-training obligation before any User content is processed through it.
                    </p>

                    {/* 3 */}
                    <h2 className={H2}>3. We Do Not Sell Your Data</h2>
                    <p className={P}>
                        We do not sell your personal information, and we do not share it for
                        cross-context behavioural advertising. PAI runs no advertising, and we do not
                        transfer your data to advertisers, data brokers or any third party for their own
                        marketing purposes. The only third parties that ever receive your data are the
                        sub-processors listed in Section 8, who process it solely to provide the Service
                        to you.
                    </p>

                    {/* 4 */}
                    <h2 className={H2}>4. Where Your Data Lives</h2>
                    <h3 className={H3}>4.1 Server location</h3>
                    <p className={P}>
                        All User data — including your account information, content, files and
                        operational logs — is stored and processed on servers located in the United
                        States (Google Cloud Platform, region us-central1, Iowa).
                    </p>
                    <p className={P}>
                        Requests you send for AI processing — messages, documents and other content
                        you submit for analysis — are handled by Google Vertex AI in Google Cloud’s
                        global region. This means an individual AI request may be processed in a
                        Google data centre outside the us-central1 region named above. The same
                        safeguards described in Section 4.2 below apply to this processing.
                    </p>
                    <h3 className={H3}>4.2 International transfers</h3>
                    <p className={P}>
                        If you are located in the European Economic Area (EEA), the United Kingdom or
                        Switzerland, the storage and processing of your data in the United States
                        constitutes an international transfer of personal data. We rely on Standard
                        Contractual Clauses (SCCs), as adopted by the European Commission, incorporated
                        into our Data Processing Agreements with each sub-processor, as the legal
                        mechanism for these transfers.
                    </p>
                    <p className={P}>
                        Further detail on international transfers, the legal basis for processing, and
                        your rights under the General Data Protection Regulation (GDPR) is set out in
                        our Privacy Policy.
                    </p>

                    {/* 5 */}
                    <h2 className={H2}>5. What Happens When You Delete Your Account</h2>
                    <h3 className={H3}>5.1 User-initiated deletion</h3>
                    <p className={P}>
                        You may delete your account at any time using the account deletion feature
                        within the application. Upon deletion:
                    </p>
                    <ol className="mt-4 space-y-3">
                        <Clause mark="a">
                            All your content — messages, documents, files, voice recordings, notes,
                            contacts, calendar data and financial records — is permanently and
                            irreversibly deleted from our production systems.
                        </Clause>
                        <Clause mark="b">
                            Your account credentials and profile information are permanently removed.
                        </Clause>
                        <Clause mark="c">
                            Operational logs that contain your content — the AI-processing and security
                            logs described in Section 6 — are purged immediately, ahead of the retention
                            periods that would otherwise apply. Diagnostic and security logs that merely
                            reference your account, without containing your content, are not deleted
                            immediately; they are retained only for the limited period described in
                            Section 5.2 below, are never used to reconstruct your deleted content, and
                            are then deleted automatically.
                        </Clause>
                        <Clause mark="d">
                            Backup systems are purged of your data within a short, fixed period as
                            part of our routine backup rotation. These backups are maintained solely
                            for disaster recovery and are never used to restore an individual account.
                        </Clause>
                    </ol>
                    <p className={P}>
                        <strong className="font-bold text-white">Your subscription.</strong>{' '}
                        Deleting your account also cancels any active subscription immediately and
                        removes your stored payment method.
                    </p>
                    <p className={P}>
                        <strong className="font-bold text-white">Content you shared with others.</strong>{' '}
                        If you created a ticket inside another user’s shared workspace — for example, on
                        a colleague’s board — it remains there after your account is deleted, shown as
                        authored by a deleted user rather than removed. Comments you wrote on other
                        users’ boards are deleted along with your account, like any other content of
                        yours. Any board, financial book, note, team or shared conversation that you
                        created yourself is deleted for everyone in it when you delete your account.
                        Records that another user created about you, such as a contact card, remain
                        theirs, with any link to your account removed.
                    </p>
                    <h3 className={H3}>5.2 What we retain after deletion</h3>
                    <p className={P}>After account deletion, we retain only:</p>
                    <ul className={UL}>
                        <li className={LI}>Anonymised, aggregate usage statistics that cannot be linked back to you or your account (for example, total message counts across all users).</li>
                        <li className={LI}>Records required by applicable law (for example, financial transaction records required for tax compliance), for the minimum period required by law and no longer.</li>
                        <li className={LI}>The record of the consent you gave when you created your account, retained as evidence that it was lawfully obtained (GDPR Article 7(1)), on the legal basis described in Article 17(3)(e).</li>
                        <li className={LI}>Operational and security logs that reference your account, retained for a limited period and then deleted automatically as part of our standard logging practice. These logs include a record that your account was deleted.</li>
                    </ul>
                    <h3 className={H3}>5.3 Subscription termination by the Owner</h3>
                    <p className={P}>
                        If we terminate your account under the Terms and Conditions, the same deletion
                        process applies. We will notify you before termination and provide a reasonable
                        opportunity to export your data using the built-in data export feature, except
                        where termination is for cause (such as a violation of the acceptable use terms)
                        and prior notice would be impracticable.
                    </p>

                    {/* 6 */}
                    <h2 className={H2}>6. Data Retention (Active Accounts)</h2>
                    <p className={P}>
                        The retention periods in this Section apply only while your account is active.
                        They govern how long certain internal operational records are kept. They do not
                        limit the content in your account — your messages, documents, notes and other
                        content remain available to you for as long as your account exists, until you
                        choose to delete them. If you delete your account, Section 5 applies and
                        everything is deleted regardless of the periods below.
                    </p>
                    <dl className="mt-6 rounded-card border border-pai-hairline bg-white/[0.02] px-5 py-5">
                        <Pair term="AI processing logs">
                            When you use AI features, we keep an internal record of the request to
                            calculate your usage and apply your plan limits. The text content of these
                            logs is deleted after 90 days. Usage figures (such as token count and cost)
                            remain linked to your account while it remains active to provide your energy
                            history; such figures are anonymised only upon account deletion (see Section
                            5.2).
                        </Pair>
                        <Pair term="Security logs">
                            Sign-in and device records, used to detect and investigate security issues,
                            are kept for 12 months and then deleted.
                        </Pair>
                    </dl>
                    <p className={P}>
                        These periods reflect our principle of keeping the least data for the shortest
                        time necessary. We may adjust them where required to provide the Service or to
                        comply with a legal obligation, and will update this Addendum accordingly.
                    </p>

                    {/* 7 */}
                    <h2 className={H2}>7. Data Breach Notification</h2>
                    <p className={P}>
                        In the event of a security breach that results in the accidental or unlawful
                        destruction, loss, alteration, unauthorised disclosure of, or access to your
                        personal data:
                    </p>
                    {/* Lettering is (e)(f)(g) in the source PDF — reproduced verbatim. */}
                    <ol className="mt-4 space-y-3">
                        <Clause mark="e">
                            We will notify the relevant supervisory authority within 72 hours of
                            becoming aware of the breach, as required by applicable data-protection law.
                        </Clause>
                        <Clause mark="f">
                            We will notify you without undue delay if the breach is likely to result in
                            a high risk to your rights and freedoms. Notification will be sent to the
                            email address associated with your account and, where available, through the
                            application itself and your linked messaging channels (WhatsApp, Telegram).
                        </Clause>
                        <Clause mark="g">
                            The notification will describe, in clear and plain language, the nature of
                            the breach, the data affected, the likely consequences, and the measures we
                            have taken or propose to take to address it.
                        </Clause>
                    </ol>
                    <p className={P}>
                        We maintain an internal incident response plan that is tested and reviewed
                        regularly.
                    </p>

                    {/* 8 */}
                    <h2 className={H2}>8. Sub-Processors</h2>
                    <p className={P}>
                        The following third-party companies process User data on our behalf in order to
                        provide the Service. Each operates under a Data Processing Agreement with us,
                        including Standard Contractual Clauses where applicable.
                    </p>
                    <dl className="mt-6 rounded-card border border-pai-hairline bg-white/[0.02] px-5 py-5">
                        <Pair term="Google Cloud Platform">
                            All application data: hosting, database, file storage and authentication.
                            Infrastructure provider for the entire service.
                        </Pair>
                        <Pair term="Google Gemini API (Vertex AI)">
                            User content sent to the AI for processing: messages, documents, images,
                            voice transcriptions and text embeddings. Our primary AI processor.
                            Processed in Google Cloud’s global region, which may include data centres
                            outside the United States (see Section 4.1).
                        </Pair>
                        <Pair term="Google (Gmail)">
                            Outgoing transactional email — verification codes, receipts and login alerts
                            — sent to your email address.
                        </Pair>
                        <Pair term="iubenda">
                            Your account identifier, email address, name, and the exact wording of the
                            terms you accepted, stored as evidence that your consent was lawfully
                            obtained. iubenda also hosts and versions our Privacy Policy, Cookie Policy
                            and Terms and Conditions, and operates the cookie-consent banner on our
                            website.
                        </Pair>
                        <Pair term="Stripe">
                            Billing identity, subscription status and payment processing. Card details
                            are entered on Stripe’s own pages and never reach our servers.
                        </Pair>
                        <Pair term="Meta (WhatsApp Business Platform)">
                            Messages, voice notes and files from users who choose to connect their
                            WhatsApp account. Users opt in by linking their account.
                        </Pair>
                        <Pair term="Telegram">
                            Messages, voice notes and files from users who choose to connect their
                            Telegram account. Users opt in by linking their account.
                        </Pair>
                    </dl>
                    <p className={P}>
                        Opting in to messaging channels is voluntary. Meta and Telegram process your
                        data only if you choose to link those accounts. The core PAI service functions
                        without them.
                    </p>
                    <p className={P}>
                        We will update this list if we add or replace a sub-processor and will notify
                        Users of material changes via email or in-app notification before the change
                        takes effect. The current version of this list is always available at this URL.
                    </p>

                    {/* 9 */}
                    <h2 className={H2}>9. Human Access to Your Content</h2>
                    <p className={P}>
                        No employee, founder or contractor of The Title AI LLC views, reads, listens to,
                        copies or exports your content in the normal course of operations. The service
                        is designed to function without any human accessing User content.
                    </p>
                    <p className={P}>Human access to User content is permitted only when:</p>
                    <ul className={UL}>
                        <li className={LI}>you have made an explicit request or given explicit consent that cannot be fulfilled without such access (for example, a support request you ask us to investigate); or</li>
                        <li className={LI}>access is strictly necessary to meet a lawful obligation or to protect the security and integrity of the service (for example, responding to a valid legal order or investigating a security incident).</li>
                    </ul>
                    <p className={P}>
                        In either case, access is limited to the minimum data needed, for the minimum
                        time needed, and is logged.
                    </p>

                    {/* 10 */}
                    <h2 className={H2}>10. Changes to This Addendum</h2>
                    <p className={P}>
                        We may update this Addendum from time to time. If we make material changes — in
                        particular, changes to AI processing, data location, the no-training commitment,
                        retention periods, or sub-processors — we will notify you at least 30 days
                        before the change takes effect, by email and/or in-app notification. Continued
                        use of the Service after the effective date constitutes acceptance of the
                        updated Addendum. If you do not agree with the changes, you may terminate your
                        account and export your data before the effective date.
                    </p>

                    {/* 11 */}
                    <h2 className={H2}>11. Contact</h2>
                    <p className={P}>
                        Questions about this Addendum or about how your data is handled can be directed
                        to:
                    </p>
                    <address className="mt-4 not-italic text-[15px] leading-[1.75] text-pai-body">
                        The Title AI LLC<br />
                        Email:{' '}
                        <a
                            href="mailto:admin@workbypai.com"
                            className="text-pai-blue underline underline-offset-2 hover:text-pai-blue-tint"
                        >
                            admin@workbypai.com
                        </a>
                        <br />
                        Address: 30 N Gould St, Ste R, Sheridan, WY 82801, USA
                    </address>

                    <p className="mt-12 border-t border-pai-hairline pt-6 font-mono text-[12px] text-pai-faint">
                        PAI Data &amp; AI Addendum · Version 1.2 · The Title AI LLC
                    </p>
                </article>

                <LegalLinks className="mt-8" />
            </div>
        </div>
    );
};

export default DataAiAddendumPage;
